import React from 'react';
import { Helmet } from 'react-helmet';

const EFFECTIVE_DATE = 'July 31, 2026';
const COMPANY = 'DI SW VENTURES LLC';
const COMPANY_ADDRESS = '30 N Gould St #42242, Sheridan, WY 82801, USA';
const CONTACT_EMAIL = 'admin@quantape.com';

// Standalone pages (rendered outside the app's QueryClientProvider), so no
// shared Header/Footer here — minimal self-contained layout with inline styles.
const S = {
  page: { minHeight: '100vh', background: '#ffffff', color: '#1f2937' },
  header: {
    borderBottom: '1px solid #e5e7eb',
    padding: '16px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  brand: { fontWeight: 700, fontSize: '18px', color: '#111827', textDecoration: 'none' },
  main: { maxWidth: '760px', margin: '0 auto', padding: '32px 24px 64px', lineHeight: 1.65 },
  h1: { fontSize: '28px', fontWeight: 700, margin: '0 0 4px' },
  date: { color: '#6b7280', fontSize: '14px', marginBottom: '28px' },
  h2: { fontSize: '20px', fontWeight: 600, margin: '28px 0 8px' },
  p: { margin: '0 0 12px' },
  ul: { margin: '0 0 12px', paddingLeft: '22px' },
  callout: {
    background: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    padding: '12px 16px',
    margin: '0 0 16px',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px', margin: '0 0 12px' },
  th: { textAlign: 'left', borderBottom: '2px solid #e5e7eb', padding: '6px 8px' },
  td: { borderBottom: '1px solid #f3f4f6', padding: '6px 8px', verticalAlign: 'top' },
  footer: {
    borderTop: '1px solid #e5e7eb',
    padding: '20px 24px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#6b7280',
  },
  footerLink: { color: '#2563eb', textDecoration: 'none', margin: '0 6px' },
  link: { color: '#2563eb' },
};

export const LegalLayout = ({ title, effectiveDate = EFFECTIVE_DATE, children }) => (
  <div style={S.page}>
    <Helmet>
      <title>{`${title} | QuantApe`}</title>
    </Helmet>
    <header style={S.header}>
      <a href="/" style={S.brand}>QuantApe</a>
      <a href="/" style={{ ...S.footerLink, fontSize: '14px' }}>← Back to app</a>
    </header>
    <main style={S.main}>
      <h1 style={S.h1}>{title}</h1>
      {effectiveDate && <p style={S.date}>Effective date: {effectiveDate}</p>}
      {children}
    </main>
    <footer style={S.footer}>
      <div>© {new Date().getFullYear()} {COMPANY}. All rights reserved.</div>
      <div style={{ marginTop: '6px' }}>
        <a href="/terms" style={S.footerLink}>Terms of Service</a>·
        <a href="/privacy" style={S.footerLink}>Privacy Policy</a>·
        <a href="/cookies" style={S.footerLink}>Cookie Policy</a>
      </div>
    </footer>
  </div>
);

const Contact = () => (
  <p style={S.p}>
    Questions? Contact us at{' '}
    <a href={`mailto:${CONTACT_EMAIL}`} style={S.link}>{CONTACT_EMAIL}</a> or by mail at{' '}
    {COMPANY}, {COMPANY_ADDRESS}.
  </p>
);

export const TermsPage = () => (
  <LegalLayout title="Terms of Service" effectiveDate="September 7, 2026">
    <p style={S.p}>
      These Terms of Service ("Terms") govern your use of QuantApe, operated by {COMPANY} ("we",
      "us"), available at quantape.com, 1stprinciples.pro, and earningsking.com (together, the
      "Service"). By accessing or using the Service you agree to these Terms. If you do not agree,
      do not use the Service.
    </p>

    <h2 style={S.h2}>1. Not Investment Advice</h2>
    <div style={S.callout}>
      <strong>The Service is for informational and educational purposes only.</strong> Nothing on
      the Service constitutes investment advice, a recommendation, an offer, or a solicitation to
      buy or sell any security or other financial instrument.
    </div>
    <p style={S.p}>
      We are not a broker-dealer, registered investment adviser, or financial planner. Screeners,
      lists, scores, signals, model portfolios, alerts, and commentary are generated automatically
      and are not tailored to your financial situation, objectives, or risk tolerance. You are
      solely responsible for your investment decisions. Investing involves risk, including the
      possible loss of principal. Consult a licensed financial professional before making
      investment decisions.
    </p>

    <h2 style={S.h2}>2. AI-Generated and Automated Content</h2>
    <p style={S.p}>
      Much of the content on the Service — including company analyses, market summaries, signals,
      and model portfolios — is produced by artificial-intelligence models and automated
      algorithms. Such content may be inaccurate, incomplete, or outdated, and may occasionally be
      wrong in significant ways. Backtested and simulated results are hypothetical: they are
      computed retroactively, do not reflect actual trading, fees, slippage, or liquidity, and have
      inherent limitations. Past performance, real or simulated, does not guarantee future results.
    </p>

    <h2 style={S.h2}>3. Market Data and Third-Party Content</h2>
    <p style={S.p}>
      Market data, fundamentals, earnings dates, news, and other content are sourced from third
      parties. We do not guarantee the accuracy, completeness, or timeliness of any data, and data
      may be delayed. The Service may link to third-party sites and communities (e.g., Discord,
      Substack) that we do not control and are not responsible for.
    </p>

    <h2 style={S.h2}>4. Accounts</h2>
    <p style={S.p}>
      Some features require signing in with a Google account. You are responsible for activity
      under your account and for keeping your credentials confidential — including personal feed
      URLs, tokens, and webhook secrets issued by the Service, which act as credentials and should
      be treated like passwords. You can rotate or revoke them in your account settings.
    </p>

    <h2 style={S.h2}>5. Acceptable Use</h2>
    <ul style={S.ul}>
      <li>No scraping, bulk downloading, or systematic extraction of data from the Service.</li>
      <li>No reselling, republishing, or redistributing Service data or content without our written permission.</li>
      <li>No interfering with, disrupting, or circumventing rate limits or security measures.</li>
      <li>No reverse engineering, except where permitted by law.</li>
      <li>No use of the Service for any unlawful purpose.</li>
    </ul>

    <h2 style={S.h2}>6. Subscriptions and Trials</h2>
    <p style={S.p}>
      Certain features may be offered on a trial basis or require a paid subscription. Feature
      availability, pricing, and plan contents may change. We may add, modify, or discontinue
      features at any time. Some features, including the MCP Server, are offered on a pay-per-call
      basis rather than by subscription; see Section 7.
    </p>

    <h2 id="mcp" style={S.h2}>7. MCP Server and Programmatic Access</h2>
    <p style={S.p}>
      The MCP Server at mcp.quantape.com lets third-party AI assistants and other clients that
      implement the Model Context Protocol query the Service on your behalf. Everything in these
      Terms, including Sections 1 to 3 (not investment advice, AI-generated content, third-party
      data), applies to data obtained through it. Daily free allowances, available tools and prices
      may change or be withdrawn at any time. A session token you use with the MCP Server is your
      credential: keep it confidential, and anything done with it is attributed to your account.
      The assistant or client you connect is not provided by us and is governed by its own terms.
    </p>
    <p style={S.p}>
      Beyond the free allowance, each metered call is paid individually in USDC on the Base network
      using the x402 protocol.
    </p>
    <ul style={S.ul}>
      <li>A payment is final once settled on-chain and is not refundable, including for data you consider stale, incomplete or wrong.</li>
      <li>A call that fails is not charged.</li>
      <li>Network fees, wallet security and the correctness of the wallet you pay from are your responsibility.</li>
      <li>We never hold your private keys and do not custody funds on your behalf.</li>
      <li>Payments on a test network (currently Base Sepolia) have no monetary value and confer no rights.</li>
      <li>The price of each call is shown, in USDC, before any payment is requested.</li>
    </ul>
    <p style={S.p}>In addition to Section 5:</p>
    <ul style={S.ul}>
      <li>No bulk extraction, caching for redistribution, or resale of list or market data obtained through the MCP Server beyond your own use within the connected assistant.</li>
      <li>No circumventing allowances or rate limits, for example by rotating IP addresses or sharing session tokens.</li>
      <li>We may rate-limit, suspend or revoke access at any time.</li>
    </ul>

    <h2 style={S.h2}>8. Intellectual Property</h2>
    <p style={S.p}>
      The Service, including its software, design, and content (other than third-party data and
      content you provide), is owned by {COMPANY} and protected by intellectual-property laws. We
      grant you a limited, non-exclusive, non-transferable license to use the Service for personal,
      non-commercial purposes.
    </p>

    <h2 style={S.h2}>9. Termination</h2>
    <p style={S.p}>
      You may stop using the Service at any time. We may suspend or terminate your access if you
      violate these Terms or if we discontinue the Service. Sections 1–3 and 10–12 survive
      termination.
    </p>

    <h2 style={S.h2}>10. Disclaimer of Warranties</h2>
    <p style={S.p}>
      THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
      IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY,
      AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE,
      OR THAT DATA WILL BE ACCURATE OR CURRENT.
    </p>

    <h2 style={S.h2}>11. Limitation of Liability</h2>
    <p style={S.p}>
      TO THE MAXIMUM EXTENT PERMITTED BY LAW, {COMPANY.toUpperCase()} SHALL NOT BE LIABLE FOR ANY
      INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY TRADING OR
      INVESTMENT LOSSES, LOST PROFITS, OR LOSS OF DATA, ARISING FROM OR RELATED TO YOUR USE OF THE
      SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE GREATER OF $100 OR THE
      AMOUNTS YOU PAID US IN THE TWELVE MONTHS PRECEDING THE CLAIM.
    </p>

    <h2 style={S.h2}>12. Indemnification</h2>
    <p style={S.p}>
      You agree to indemnify and hold harmless {COMPANY} from claims arising out of your use of the
      Service or violation of these Terms.
    </p>

    <h2 style={S.h2}>13. Governing Law</h2>
    <p style={S.p}>
      These Terms are governed by the laws of the State of Wyoming, without regard to
      conflict-of-laws principles.
    </p>

    <h2 style={S.h2}>14. Changes to These Terms</h2>
    <p style={S.p}>
      We may update these Terms from time to time. The current version, with its effective date,
      will always be posted at this page. Continued use of the Service after changes take effect
      constitutes acceptance.
    </p>

    <h2 style={S.h2}>15. Contact</h2>
    <Contact />
  </LegalLayout>
);

export const PrivacyPage = () => (
  <LegalLayout title="Privacy Policy" effectiveDate="October 7, 2026">
    <p style={S.p}>
      This Privacy Policy describes how {COMPANY} ("we", "us") handles information when you use
      QuantApe at quantape.com, 1stprinciples.pro, or earningsking.com (the "Service"). The short
      version: we collect what's needed to run the Service, we use privacy-respecting first-party
      analytics, we don't run third-party ad or analytics trackers, and we never sell your data.
    </p>

    <h2 style={S.h2}>1. Information We Collect</h2>
    <p style={S.p}><strong>Account information.</strong> If you sign in with Google, we receive
      your email address, name, and profile picture from Google.</p>
    <p style={S.p}><strong>Content and settings you provide.</strong> Watchlists, labels, notes,
      screening preferences, and display settings; notification settings including webhook URLs you
      configure (e.g., Discord or Slack) and web-push subscriptions; personal feed tokens for
      calendar and signal feeds; and your email-update subscription status.</p>
    <p style={S.p}><strong>Usage analytics (first-party).</strong> We built our own lightweight
      analytics rather than using a third-party tracker. It records a random anonymous identifier
      (stored in your browser's localStorage), a per-visit session identifier, pages viewed and
      in-app events, device type, browser user agent, referring page, and approximate location
      (city, region, and country, derived by our hosting provider from the request). We do not
      store your IP address. Known bots and local development traffic are excluded.</p>
    <p style={S.p}><strong>MCP Server usage.</strong> For each call to the MCP Server we record the
      time, the tool and list requested, the outcome and latency, and an identifier for the caller:
      your account email when you connect with a session token, otherwise a salted hash of the
      connecting IP address (the raw IP address is not stored). For paid calls we also record the
      paying wallet address, transaction hash, amount and network; these are public on the
      blockchain by design.</p>

    <h2 style={S.h2}>2. How We Use Information</h2>
    <ul style={S.ul}>
      <li>To provide and operate the Service, including syncing your watchlists and preferences across devices.</li>
      <li>To deliver notifications you configure (webhooks, web push, calendar/signal feeds) and emails you opt into.</li>
      <li>To understand how the Service is used and improve it.</li>
      <li>To secure the Service and prevent abuse.</li>
    </ul>

    <h2 style={S.h2}>3. Sharing</h2>
    <p style={S.p}>We do not sell personal information, and we do not share it with advertisers.
      We share information only with:</p>
    <ul style={S.ul}>
      <li><strong>Service providers</strong> that host and operate the Service on our behalf:
        Cloudflare (hosting, storage, databases, and email delivery for updates you opt into) and
        Google (sign-in authentication).</li>
      <li><strong>Recipients you configure.</strong> If you set up a Discord/Slack webhook or
        subscribe a calendar app to your personal feed URL, data flows to that destination at your
        direction.</li>
      <li><strong>Legal requirements</strong>, if we are required to by law.</li>
      <li>Payment verification and settlement for the MCP Server is performed by a third-party
        x402 facilitator, which receives the signed payment authorization but not your account
        data.</li>
    </ul>

    <h2 style={S.h2}>4. Data Retention</h2>
    <p style={S.p}>
      We keep account data while your account is active. Usage analytics are retained in aggregate
      or pseudonymous form for product-improvement purposes. You can request deletion at any time
      (see below). MCP Server call records are kept while needed for allowance accounting, billing
      reconciliation and abuse prevention, and records of paid calls for as long as financial
      record-keeping requires; after that they are deleted or reduced to aggregates.
    </p>

    <h2 style={S.h2}>5. Your Rights and Choices</h2>
    <ul style={S.ul}>
      <li>Access, correct, or delete your data by emailing{' '}
        <a href={`mailto:${CONTACT_EMAIL}`} style={S.link}>{CONTACT_EMAIL}</a>. Depending on where
        you live (e.g., EEA/UK under GDPR, California under CCPA), you may have statutory rights to
        access, portability, correction, and deletion — we honor these requests regardless of
        location.</li>
      <li>Unsubscribe from email updates via the link in any email or in your notification settings.</li>
      <li>Disable web-push notifications in your browser or in the Service's notification settings.</li>
      <li>Rotate or revoke personal feed tokens and webhook settings in the Service at any time.</li>
      <li>Clear the anonymous analytics identifier by clearing your browser's site data (see the{' '}
        <a href="/cookies" style={S.link}>Cookie Policy</a>).</li>
    </ul>

    <h2 style={S.h2}>6. Security</h2>
    <p style={S.p}>
      Data is transmitted over HTTPS and stored with Cloudflare's infrastructure. Personal feed
      URLs and webhook secrets act as credentials — keep them private and rotate them if exposed.
    </p>

    <h2 style={S.h2}>7. Children</h2>
    <p style={S.p}>
      The Service is not directed to individuals under 18, and we do not knowingly collect personal
      information from children.
    </p>

    <h2 style={S.h2}>8. International Users</h2>
    <p style={S.p}>
      We are based in the United States and process data there and on Cloudflare's global network.
      By using the Service, you understand your information may be processed in the United States.
    </p>

    <h2 style={S.h2}>9. Changes to This Policy</h2>
    <p style={S.p}>
      We may update this policy from time to time; the current version and effective date will
      always be posted here. Material changes will be highlighted in the Service.
    </p>

    <h2 style={S.h2}>10. Contact</h2>
    <Contact />
  </LegalLayout>
);

export const CookiePolicyPage = () => (
  <LegalLayout title="Cookie Policy">
    <p style={S.p}>
      This policy explains how QuantApe uses cookies and similar browser-storage technologies.
      The short version: <strong>we do not use advertising cookies or third-party analytics
      trackers.</strong> We use your browser's localStorage for sign-in, preferences, and our own
      first-party analytics.
    </p>

    <h2 style={S.h2}>1. Browser Storage We Use</h2>
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Category</th>
          <th style={S.th}>Examples</th>
          <th style={S.th}>Purpose</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={S.td}>Authentication</td>
          <td style={S.td}>authToken, userId</td>
          <td style={S.td}>Keep you signed in between visits.</td>
        </tr>
        <tr>
          <td style={S.td}>Preferences</td>
          <td style={S.td}>userPreferences, userSettings, columnsConfig, colorSettings, stockNotes</td>
          <td style={S.td}>Remember your watchlist, display, and screening settings.</td>
        </tr>
        <tr>
          <td style={S.td}>First-party analytics</td>
          <td style={S.td}>qa_anonymous_id, qa_visit_count</td>
          <td style={S.td}>A random identifier and visit counter for our own usage analytics. Not
            shared with third parties and not linked to advertising.</td>
        </tr>
      </tbody>
    </table>

    <h2 style={S.h2}>2. Third-Party Cookies</h2>
    <p style={S.p}>
      If you sign in with Google, Google may set cookies on its own domains as part of the
      authentication flow, governed by{' '}
      <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={S.link}>
        Google's Privacy Policy
      </a>. We do not embed any advertising or third-party analytics scripts.
    </p>

    <h2 style={S.h2}>3. Managing Storage</h2>
    <p style={S.p}>
      You can clear or block this storage through your browser's site-data settings. Clearing it
      signs you out and resets local preferences (server-saved settings are restored when you sign
      back in), and issues a fresh anonymous analytics identifier.
    </p>

    <h2 style={S.h2}>4. Changes and Contact</h2>
    <p style={S.p}>
      We'll update this page if our use of browser storage changes.
    </p>
    <Contact />
  </LegalLayout>
);
